// Reusable "Send Pre-Set Text" dialog. Called from the lead detail composer
// or anywhere a lead context is in scope.

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Send } from 'lucide-react';
import {
  listPresetTexts,
  sendPresetText,
  previewPresetText,
} from '../api/marketingPresetTexts.js';

export default function SendPresetTextDialog({ leadId, onClose, onSent }) {
  const [presetId, setPresetId] = useState('');
  const [body, setBody] = useState('');
  const [rendered, setRendered] = useState('');
  const [busy, setBusy] = useState(false);

  // Escape key closes (a11y).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data } = useQuery({
    queryKey: ['marketing-preset-texts'],
    queryFn: () => listPresetTexts(),
  });
  const presets = data?.presets || [];

  useEffect(() => {
    if (!presetId) { setBody(''); setRendered(''); return; }
    const p = presets.find((x) => x._id === presetId);
    if (p) setBody(p.body);
  }, [presetId, presets]);

  useEffect(() => {
    let cancelled = false;
    if (!body) { setRendered(''); return; }
    const t = setTimeout(async () => {
      try {
        const r = await previewPresetText(body, leadId);
        if (!cancelled) setRendered(r.rendered);
      } catch (_) {}
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [body, leadId]);

  async function send() {
    if (!presetId) { toast.error('Pick a preset'); return; }
    setBusy(true);
    try {
      const r = await sendPresetText(presetId, leadId);
      if (!r.ok) {
        toast.error(r.error || 'send failed');
        return;
      }
      toast.success('Sent');
      onSent?.(r);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-[#0A0A0F] border border-white/10 rounded-lg w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-medium">Send Pre-Set Text</div>
          <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-xs text-white/60">Template</label>
          <select
            value={presetId}
            onChange={(e) => setPresetId(e.target.value)}
            className="w-full bg-[#0A0A0F] border border-white/10 rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">— choose a preset —</option>
            {presets.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          {body && (
            <div className="rounded-md border border-white/10 bg-white/3 p-3">
              <div className="text-xs text-white/50 mb-1">Preview</div>
              <div className="text-sm whitespace-pre-wrap">{rendered || body}</div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-sm">Cancel</button>
          <button
            onClick={send}
            disabled={!presetId || busy}
            className="px-3 py-1.5 rounded-md bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}
